import { useQuery } from '@tanstack/react-query';
import {
  type LocationCountryCode,
  LocationService,
  type LocationLocality,
  type LocationRegion,
} from 'src/api/generated';
import type { ApiResponse } from 'src/hooks/api/api';

const LOCATION_STALE_TIME = 1000 * 60 * 60 * 24; // 24 hours

/**
 * Response object returned by the useRegions hook.
 * Contains region data and query state.
 */
export interface UseRegionsResponse extends ApiResponse {
  /** Array of region items. */
  regions: LocationRegion[];
}

/**
 * Response object returned by the useLocalities hook.
 * Contains locality data and query state.
 */
export interface UseLocalitiesResponse extends ApiResponse {
  /** Array of locality items. */
  localities: LocationLocality[];
}

/**
 * Hook for fetching regions by country code.
 * Returns region data with loading and error states.
 *
 * @param countryCode - Optional country code override; when omitted, backend resolves tenant default country
 * @param enabled Whether to fetch the catalog for this editor.
 * @returns Response object containing regions array, loading state, error, and refetch function
 */
export const useRegions = (
  countryCode?: LocationCountryCode,
  enabled = true
): UseRegionsResponse => {
  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery<LocationRegion[], Error>({
    queryKey: ['location', 'regions', countryCode],
    queryFn: () => LocationService.listRegions(countryCode),
    staleTime: LOCATION_STALE_TIME,
    enabled,
  });

  return {
    regions: data ?? [],
    loading,
    error: error ?? null,
    refetch,
  };
};

/**
 * Hook for fetching localities by country code.
 * Returns locality data with loading and error states.
 *
 * @param countryCode - Optional country code override; when omitted, backend resolves tenant default country
 * @param enabled Whether to fetch the catalog for this editor.
 * @returns Response object containing localities array, loading state, error, and refetch function
 */
export const useLocalities = (
  countryCode?: LocationCountryCode,
  enabled = true
): UseLocalitiesResponse => {
  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery<LocationLocality[], Error>({
    queryKey: ['location', 'localities', countryCode],
    queryFn: () => LocationService.listLocalities(countryCode),
    staleTime: LOCATION_STALE_TIME,
    enabled,
  });

  return {
    localities: data ?? [],
    loading,
    error: error ?? null,
    refetch,
  };
};
